import React, { useState, useEffect } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, "0")}`
}

const CreateUpdateDialog = ({ isOpen, onClose, controller }) => {
  const [updateName, setUpdateName] = useState("")
  const [description, setDescription] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [taskStatus, setTaskStatus] = useState(null)
  const [error, setError] = useState(null)
  const [timeLeft, setTimeLeft] = useState(0)
  const [isExpired, setIsExpired] = useState(false)

  useEffect(() => {
    let timer
    if (taskStatus?.result?.expiresIn && !isExpired) {
      // Convert minutes to seconds for countdown
      setTimeLeft(taskStatus.result.expiresIn * 60)

      timer = setInterval(() => {
        setTimeLeft((current) => {
          if (current <= 1) {
            clearInterval(timer)
            setIsExpired(true)
            return 0
          }
          return current - 1
        })
      }, 1000)
    }

    return () => {
      if (timer) {
        clearInterval(timer)
      }
    }
  }, [taskStatus?.result?.expiresIn])

  const handleCreate = async () => {
    if (!updateName.trim()) {
      setError("Update name is required")
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const result = await controller.createUpdate({
        updateName,
        description,
        onResponse: (status) => {
          setTaskStatus(status)
          if (
            status.status === "completed" ||
            status.status === "failed" ||
            status.status === "error"
          ) {
            setIsLoading(false)
          }
        },
      })
      if (result.error) {
        setError(result.error)
      }
    } catch (err) {
      setError("Failed to create update: " + err.message)
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setUpdateName("")
    setDescription("")
    setTaskStatus(null)
    setError(null)
    setIsLoading(false)
    setTimeLeft(0)
    setIsExpired(false)
    onClose()
  }

  const isDownloadReady =
    taskStatus?.status === "completed" && taskStatus?.result?.download

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin_primary"></div>

          <Typography variant="admin_body">
            {taskStatus?.activity || "Creating update..."}
          </Typography>
        </div>
      )
    }

    if (isDownloadReady) {
      return (
        <div className="flex flex-col items-center justify-center py-8 space-y-2">
          <Button
            variant="admin_primary"
            onClick={() => window.open(taskStatus.result.download, "_blank")}
            className="w-auto"
            disabled={isExpired}
          >
            {isExpired ? "Expired" : "Download"}
          </Button>

          <Typography
            variant="admin_txt"
            className={isExpired ? "text-admin_error" : "text-admin_text_secondary"}
          >
            {isExpired
              ? "Link has expired"
              : `Link expires in: ${formatTime(timeLeft)}`}
          </Typography>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        <Input
          variant="admin"
          label="Update Name"
          value={updateName}
          onChange={(e) => setUpdateName(e.target.value)}
          placeholder="Enter update name..."
          required
          className="w-full"
        />

        <Input
          variant="admin"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter description..."
          multiline
          className="w-full"
        />
      </div>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      variant="admin_centered"
      className="w-full md:w-[600px] bg-admin_paper"
      // headerContent={
      //   <div className="flex justify-end gap-4 mr-10">
      //     {!isLoading && !isDownloadReady && (
      //       <Button
      //         variant="admin_primary"
      //         onClick={handleCreate}
      //         disabled={!updateName.trim()}
      //       >
      //         Create Update
      //       </Button>
      //     )}
      //   </div>
      // }
    >
      <div className="px-2 pb-2 space-y-6">
        <div className="text-center space-y-2">
          <Typography 
            component="h1"
            variant="admin_h4"
          >
            Site Update
          </Typography>

          {isDownloadReady && (
            <>
              <Typography variant="admin_body_dim">
                {updateName}
              </Typography>

              {description && (
                <Typography variant="admin_body_dim">
                  {description}
                </Typography>
              )}
            </>
          )}
        </div>

        {error && (
          <div className="p-4 bg-admin_error/20 text-admin_error rounded-md">{error}</div>
        )}

        {renderContent()}

        <div className="flex justify-end">
          {!isLoading && !isDownloadReady && (
            <Button
              variant="admin_primary"
              onClick={handleCreate}
              disabled={!updateName.trim()}
            >
              Create Update
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default CreateUpdateDialog
